import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Menu, IndianRupee, Calendar, TrendingUp, ArrowUpRight, ArrowDownRight, PlusCircle, BarChart3,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function Dashboard({ incomeData, toggleSidebar }) {
  const [timePeriod, setTimePeriod] = useState('7d');

  const totalIncome = incomeData.reduce((sum, entry) => sum + entry.amount, 0);
  const thisMonth = incomeData.filter(e => e.date.startsWith(new Date().toISOString().slice(0, 7))).reduce((sum, entry) => sum + entry.amount, 0);

  const lastMonthDate = new Date();
  lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
  const lastMonthString = lastMonthDate.toISOString().slice(0, 7);
  const lastMonth = incomeData.filter(e => e.date.startsWith(lastMonthString)).reduce((sum, entry) => sum + entry.amount, 0);

  const growth = lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth * 100) : 0;

  const getTrendData = () => {
    let days = 7;
    let label = "Last 7 Days";
    if(timePeriod === '30d') { days = 30; label = "Last 30 Days"; }
    else if(timePeriod === '90d') { days = 90; label = "Last 3 Months"; }
    else if(timePeriod === '365d') { days = 365; label = "Last Year"; }

    const filteredData = incomeData
      .filter(e => new Date(e.date) >= new Date(Date.now() - days * 24 * 60 * 60 * 1000))
      .reduce((acc, entry) => {
        const existing = acc.find(item => item.date === entry.date);
        if (existing) existing.amount += entry.amount;
        else acc.push({ date: entry.date, amount: entry.amount });
        return acc;
      }, [])
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return { data: filteredData, label };
  };

  const { data: trendData, label: trendLabel } = getTrendData();

  const GlassTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass-tooltip">
          <div className="tooltip-content">
            <p className="tooltip-label">{label}</p>
            <p className="tooltip-value">₹{payload[0].value.toLocaleString('en-IN')}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomActiveDot = (props) => {
    const { cx, cy } = props;
    return (
      <g>
        <motion.circle cx={cx} cy={cy} r={8} fill="#3B82F6" stroke="#1E293B" strokeWidth={3} initial={{ scale: 0 }} animate={{ scale: 1 }} />
        <motion.circle cx={cx} cy={cy} r={6} fill="#3B82F6" initial={{ scale: 0, opacity: 0 }} animate={{ scale: [1, 2.5, 1], opacity: [0.8, 0, 0.8] }} transition={{ duration: 2, repeat: Infinity }} />
      </g>
    );
  };

  return (
    <motion.div className="page dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="page-header-enhanced">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button className="mobile-menu-btn" onClick={toggleSidebar}>
            <Menu size={24} />
          </button>
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle">Your income overview • {incomeData.length} total entries</p>
          </div>
        </div>

        <div className="header-actions">
          <div className="period-selector">
            {[{ key: '7d', label: '7D' }, { key: '30d', label: '1M' }, { key: '90d', label: '3M' }, { key: '365d', label: '1Y' }].map(period => (
              <button key={period.key} className={`period-btn ${timePeriod === period.key ? 'active' : ''}`} onClick={() => setTimePeriod(period.key)}>
                {period.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="stats-grid-fixed">
        <motion.div className="stat-card-fixed primary" whileHover={{ y: -4 }}>
          <div className="stat-icon-large"><IndianRupee size={32} /></div>
          <div className="stat-content-large">
            <p className="stat-label-large">Total Income</p>
            <h2 className="stat-value-huge">₹{totalIncome.toLocaleString('en-IN')}</h2>
            <p className="stat-meta">{incomeData.length} total entries</p>
          </div>
        </motion.div>
        <motion.div className="stat-card-fixed" whileHover={{ y: -4 }}>
          <div className="stat-icon-large secondary"><Calendar size={28} /></div>
          <div className="stat-content-large">
            <p className="stat-label-large">This Month</p>
            <h3 className="stat-value-large">₹{thisMonth.toLocaleString('en-IN')}</h3>
            <div className={`growth-indicator ${growth >= 0 ? 'positive' : 'negative'}`}>
              {growth >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
              <span>{Math.abs(growth).toFixed(1)}% vs last</span>
            </div>
          </div>
        </motion.div>
        <motion.div className="stat-card-fixed" whileHover={{ y: -4 }}>
          <div className="stat-icon-large tertiary"><TrendingUp size={28} /></div>
          <div className="stat-content-large">
            <p className="stat-label-large">Average Entry</p>
            <h3 className="stat-value-large">₹{incomeData.length > 0 ? Math.round(totalIncome / incomeData.length).toLocaleString('en-IN') : 0}</h3>
            <p className="stat-meta">Across all sources</p>
          </div>
        </motion.div>
      </div>

      {trendData.length > 0 && (
        <div className="card chart-card-large">
          <div className="card-header"><h3>{trendLabel} Trend</h3></div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="date" stroke="#64748B" />
              <YAxis stroke="#64748B" />
              <Tooltip content={<GlassTooltip />} cursor={false} />
              <Area type="monotone" dataKey="amount" stroke="#3B82F6" strokeWidth={3} fill="url(#colorIncome)" activeDot={<CustomActiveDot />} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="quick-actions">
        <NavLink to="/add" className="action-card"><PlusCircle size={24} /><span>Add Income</span></NavLink>
        <NavLink to="/forecast" className="action-card"><TrendingUp size={24} /><span>View Forecast</span></NavLink>
        <NavLink to="/analytics" className="action-card"><BarChart3 size={24} /><span>Analytics</span></NavLink>
      </div>
    </motion.div>
  );
}
